import React from "react";

import { X } from "lucide-react";
import Topliner from "@/betby/assets/image/modal/modal_top.png";
import Bottomliner from "@/betby/assets/image/modal/modal_bottom.png";
import { cn } from "@/betby/lib/utils";
import { useThemeViewModel } from "@/betby/viewmodels/useThemeViewModel";

export type BetbyModalShellProps = {
  open: boolean;
  /** 覆盖层 z-index，例如网络弹窗需高于其它层 */
  zIndexClass?: string;
  /** 换皮：挂在面板容器上，用于覆盖 --betby-modal-* 或替换装饰 */
  panelClassName?: string;
  title?: React.ReactNode;
  /** 可选插图区（如 No internet 图标），换皮时由业务层传入 */
  illustration?: React.ReactNode;
  description?: React.ReactNode;
  primaryLabel: string;
  onPrimaryClick: () => void;
  showCloseButton?: boolean;
  /** 点击遮罩 / 关闭按钮时调用；与 closeOnBackdrop 配合使用 */
  onRequestClose?: () => void;
  closeOnBackdrop?: boolean;
};

/**
 * Betby 通用提示弹窗壳：装饰边框 + 主按钮。
 * 换皮时优先改外层 className 或覆盖 CSS 变量（--betby-modal-*）。
 */
export function BetbyModalShell({
  open,
  zIndexClass = "z-9999",
  panelClassName,
  title,
  illustration,
  description,
  primaryLabel,
  onPrimaryClick,
  showCloseButton = false,
  onRequestClose,
  closeOnBackdrop = false,
}: BetbyModalShellProps) {
  const { theme } = useThemeViewModel();

  if (!open) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (!closeOnBackdrop || !onRequestClose) return;
    if (e.target !== e.currentTarget) return;
    onRequestClose();
  };

  return (
    <div
      className={`fixed inset-0 ${zIndexClass} flex items-center justify-center bg-black/60 pointer-events-auto`}
      onClick={closeOnBackdrop ? handleBackdropClick : undefined}
      role="presentation"
    >
      <div
        className={cn(
          "relative w-[90%] max-w-[382px] overflow-hidden rounded-[16px] p-6 text-center",
          panelClassName,
        )}
        style={
          {
            background: `
              linear-gradient(var(--betby-modal-bg-inner), var(--betby-modal-bg-inner)) padding-box,
              linear-gradient(360deg, var(--betby-modal-border-start), var(--betby-modal-border-mid), var(--betby-modal-border-end)) border-box
            `,
            border: "1px solid transparent",
            backgroundClip: "padding-box, border-box",
            backgroundOrigin: "padding-box, border-box",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
          } as React.CSSProperties
        }
        onClick={(e) => e.stopPropagation()}
      >
        {theme === "sc" && (
          <div className="theme-sc-only absolute top-0 left-0 z-1 w-full">
            <img
              src={Topliner}
              alt=""
              width={394}
              height={100}
              className="w-full h-auto"
            />
          </div>
        )}

        {showCloseButton && onRequestClose ? (
          <button
            type="button"
            onClick={onRequestClose}
            className="absolute top-3 right-3 z-20 flex h-8 w-8 items-center justify-center rounded-full text-[#868686] outline-none transition-colors "
            aria-label="Close"
          >
            <X size={32} strokeWidth={2} />
          </button>
        ) : null}

        <div className="relative z-10 flex flex-col items-center py-2 text-center">
          {title ? (
            <h3
              className="mb-4 text-[22px] font-bold"
              style={{ color: "var(--betby-modal-text-title, #ffffff)" }}
            >
              {title}
            </h3>
          ) : null}

          {illustration ? (
            <div className="mb-4 flex w-full justify-center">
              {illustration}
            </div>
          ) : null}

          {description ? (
            <div
              className="text-[16px] font-medium leading-6"
              style={{
                color: "var(--betby-modal-text-desc, rgba(255, 255, 255, 0.8))",
              }}
            >
              {description}
            </div>
          ) : null}

          <button
            type="button"
            onClick={onPrimaryClick}
            className="z-20 mt-[32px] h-[48px] w-full max-w-[200px] rounded-[16px] text-[22px] font-bold transition-transform active:scale-95"
            style={{
              color: "var(--betby-modal-primary-text, #ffffff)",
              background:
                "linear-gradient(180deg, var(--betby-modal-primary-start) 0%, var(--betby-modal-primary-end) 100%)",
              boxShadow: "0 2px 8px var(--betby-modal-primary-shadow)",
            }}
          >
            {primaryLabel}
          </button>
        </div>

        {theme === "sc" && (
          <div className="absolute bottom-0 left-0 z-1 w-full">
            <img
              src={Bottomliner}
              alt=""
              width={394}
              height={100}
              className="w-full h-auto"
            />
          </div>
        )}
      </div>
    </div>
  );
}
